import { useEffect, useMemo, useRef } from 'react'

import { Form, type FormRef } from '@tribeplatform/react-components/Form'
import { FormSearchableSelect } from '@tribeplatform/react-components/Form/FormSearchableSelect'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthMember, useUpdateMember } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { availableTimezoneItems } from './utils/utils.js'

export const TimezoneSettings = () => {
  const { $t } = useI18n()
  const formRef = useRef<FormRef>()
  const { data: member } = useAuthMember()
  const { mutateAsync: updateMember, isLoading } = useUpdateMember()
  const defaultValues = useMemo(() => {
    return {
      timeZone: member?.timeZone,
    }
  }, [member?.timeZone])
  const updateMemberTimeZone = async (timeZone: string) => {
    try {
      await updateMember({
        id: member?.id,
        input: {
          timeZone,
        },
      })

      toast({
        title: $t({
          defaultMessage: 'Preferred timezone updated.',
          id: 'Settings.Timezone.PreferenceUpdated',
        }),
        status: 'success',
      })
    } catch (_error) {
      toast({
        title: $t({
          defaultMessage: 'Failed to update timezone.',
          id: 'Settings.Timezone.UpdateFailed',
        }),
        status: 'error',
      })
    }
  }

  useEffect(() => {
    if (formRef.current?.methods) {
      formRef.current.methods.reset(defaultValues)
    }
  }, [defaultValues])

  return (
    <Card>
      <Card.Header
        title={<T defaultMessage="Timezone" id="Generics.Timezone" />}
      />
      <Card.Content>
        <Form
          defaultValues={defaultValues}
          onSubmit={({ timeZone }) => {
            updateMemberTimeZone(timeZone)
          }}
          ref={formRef}
        >
          {({ formState: { isDirty } }) => (
            <div className="flex flex-col gap-y-5">
              <FormSearchableSelect
                name="timeZone"
                label={
                  <T
                    defaultMessage="Preferred timezone"
                    id="Settings.Timezone.Preference"
                  />
                }
                helperText={$t({
                  defaultMessage:
                    'Choose your timezone to get notifications based on your local time.',
                  id: 'Settings.Timezone.Preference.HelperText',
                })}
                options={availableTimezoneItems}
              />
              <Form.Actions>
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={!isDirty}
                  size="lg"
                >
                  <T defaultMessage="Update" id="Generics.Update" />
                </Button>
              </Form.Actions>
            </div>
          )}
        </Form>
      </Card.Content>
    </Card>
  )
}
